import React, { useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { removeLanguagePrefix, addLanguagePrefix } from '../utils/languageRouting';
import { ensureLanguage } from '../i18n';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use ref to always have the latest pathname
  const currentPathnameRef = useRef(location.pathname);
  
  // Update ref whenever location changes
  useEffect(() => {
    currentPathnameRef.current = location.pathname;
  }, [location.pathname]);
  
  const languages = [
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' },
    { code: 'pl', label: 'PL' },
    { code: 'sv', label: 'SV' }
  ];

  const handleLanguageChange = useCallback(async (langCode, event) => {
    // Stop event propagation to prevent triggering parent button clicks
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Preload the translation chunk BEFORE navigating so the next render
    // is already in the right language (no flash of EN, no second click).
    await ensureLanguage(langCode);

    const currentPathname = currentPathnameRef.current;
    const currentPath = removeLanguagePrefix(currentPathname);
    const newPath = addLanguagePrefix(currentPath, langCode);

    navigate(newPath, { replace: true });
  }, [navigate]);

  const currentLang = i18n.language;

  return (
    <div className="flex items-center space-x-2">
      <Globe size={18} className="text-stone-400" />
      <div className="flex space-x-1">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={(e) => handleLanguageChange(lang.code, e)}
            className={`px-2 py-1 text-sm font-medium transition-colors rounded ${
              currentLang === lang.code
                ? 'bg-[#519dc7] text-white'
                : 'text-stone-400 hover:text-[#519dc7]'
            }`}
          >
            {lang.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
